import { ulid } from 'ulid';

import { SECTION_KEY_ATTRIBUTE } from '~/components/core/Editor/extensions/ConditionalSection';
import type { TemplateSectionFormValue } from '~/components/GenericTemplates/types';
import type { UpdateGenericTemplateRequestTemplateSectionRequest as TemplateSectionPayload } from '~/server_shared/openapi/data-contracts';

/**
 * The template types whose editor offers conditional sections, as the design review scoped them. The
 * backend takes `sections` on every generic template type, so this is a product choice, not a request shape.
 */
export const CONDITIONAL_SECTIONS_TEMPLATE_TYPES: readonly string[] = ['email', 'note', 'notification'];

const parseBody = (html: string): HTMLElement => {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container;
};

const unwrap = (element: Element) => {
  element.replaceWith(...Array.from(element.childNodes));
};

/** The body with every section wrapper removed and its text kept. */
export const stripSectionWrappers = (html: string): string => {
  if (!html.includes(SECTION_KEY_ATTRIBUTE)) {
    return html;
  }
  const container = parseBody(html);
  container.querySelectorAll(`[${SECTION_KEY_ATTRIBUTE}]`).forEach(unwrap);
  return container.innerHTML;
};

/**
 * The body and the `sections` list the save sends, made to agree: a wrapper with no entry (a new section
 * whose dialog was cancelled) is unwrapped, and an entry whose wrapper the author deleted is dropped.
 * A pasted copy of a section repeats its key: the copy gets a new key and the same condition.
 * A condition is sent only when it changed.
 */
export const toSectionsPayload = (
  html: string,
  sections: TemplateSectionFormValue[]
): { body_template: string; sections: TemplateSectionPayload[] } => {
  const container = parseBody(html);
  const entryByKey = new Map(sections.map((section) => [section.key, section]));
  const payload: TemplateSectionPayload[] = [];
  const seenKeys = new Set<string>();
  container.querySelectorAll(`[${SECTION_KEY_ATTRIBUTE}]`).forEach((element) => {
    const key = element.getAttribute(SECTION_KEY_ATTRIBUTE) ?? '';
    const entry = entryByKey.get(key);
    if (!entry) {
      unwrap(element);
    } else if (seenKeys.has(key)) {
      const copyKey = ulid();
      element.setAttribute(SECTION_KEY_ATTRIBUTE, copyKey);
      payload.push({ key: copyKey, label: entry.label, conditions_json: entry.conditions_json });
    } else {
      seenKeys.add(key);
      const { label, conditions_json, isConditionChanged } = entry;
      payload.push(isConditionChanged ? { key, label, conditions_json } : { key, label });
    }
  });
  return {
    body_template: html.includes(SECTION_KEY_ATTRIBUTE) ? container.innerHTML : html,
    sections: payload,
  };
};
